import type { TimeEntryParsePreview } from '@fos/shared/contracts/time';
import { todayLocal } from '@fos/shared/time';
import { Check, TriangleAlert } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { formatDayShort, formatDuration, formatMoney } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Mono } from '../../../ui/Mono';
import { toast } from '../../../ui/Toast';
import { useCopilotInvalidate } from '../hooks';

const CLIENT_CODES = new Set(['client_missing', 'client_unknown', 'client_ambiguous']);

function dayLabel(date: string): string {
  return date === todayLocal() ? 'dziś' : formatDayShort(date);
}

function newKey(): string {
  const rnd = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `copilot:${rnd}`;
}

/**
 * Podgląd wpisu czasu z tekstu (MVP §7, §17 nr 8; Etap 6): AI niczego nie zapisuje — osoba widzi godziny, klienta,
 * opis i kwotę (z „Widzi stawki i kwoty”), przy niejednoznacznym kliencie wybiera „Który klient?”, a „Zapisz wpis”
 * wywołuje `time.create` z kluczem idempotencji (podwójne kliknięcie = jeden wpis).
 */
export function TimePreviewCard({
  preview,
  onChooseClient,
  choosing = false,
  onSaved,
  className,
}: {
  preview: TimeEntryParsePreview;
  /** wybór klienta z listy kandydatów (ponowny podgląd z `clientId`) */
  onChooseClient?: (clientId: string) => void;
  choosing?: boolean;
  /** zapisano wpis — `label` = „Zapisano wpis 10:20–12:00” (rozmowa Copilota zapamiętuje wynik) */
  onSaved?: (label: string) => void;
  className?: string;
}) {
  const create = trpc.time.create.useMutation();
  const invalidate = useCopilotInvalidate();
  const key = useMemo(newKey, []);
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const group = useId();
  const candidates = preview.candidates;
  const issues = preview.ambiguities.filter((a) => !CLIENT_CODES.has(a.code) || candidates.length === 0);

  const save = async () => {
    if (!preview.draft) return;
    setError(null);
    try {
      const res = await create.mutateAsync({ ...preview.draft, idempotencyKey: key });
      await invalidate.time();
      const hours = `${res.entry.start}–${res.entry.end}${res.entry.endDate ? ' (+1)' : ''}`;
      setSaved(hours);
      for (const w of res.warnings) toast.warning(w.message);
      if (res.warnings.length === 0) {
        toast.success('Dodano czas', { description: [hours, res.entry.description].filter(Boolean).join(' · ') });
      }
      onSaved?.(`Zapisano wpis ${hours}`);
    } catch (err) {
      setError(
        appErrorCode(err) === 'FORBIDDEN'
          ? 'Nie masz uprawnień do dodawania czasu.'
          : errorMessage(err, 'Nie udało się zapisać wpisu.'),
      );
    }
  };

  return (
    <div className={cn('flex flex-col gap-2 rounded-lg bg-canvas p-3', className)}>
      <Mono className="text-body-m text-fg">
        {preview.start ?? '—'} – {preview.end ?? '—'}
        {preview.endDate ? ' (+1)' : ''}
        {preview.durationSeconds ? ` · ${formatDuration(preview.durationSeconds)}` : ''}
      </Mono>
      <p className="text-caption text-fg-muted">
        {[
          dayLabel(preview.date),
          preview.client?.name ?? null,
          preview.project?.name ?? null,
          preview.description || null,
          preview.amount !== undefined && preview.billable ? formatMoney(preview.amount, preview.currency) : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>

      {candidates.length > 0 && !saved && (
        <fieldset className="flex flex-col" disabled={choosing}>
          <legend className="mb-1 text-caption font-medium text-fg-secondary">Który klient?</legend>
          {candidates.map((c) => {
            const checked = preview.client?.id === c.id;
            return (
              <label
                key={c.id}
                className="flex min-h-10 cursor-pointer items-center gap-3 border-b border-line text-body-s has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-accent"
              >
                <input
                  type="radio"
                  name={group}
                  value={c.id}
                  checked={checked}
                  onChange={() => onChooseClient?.(c.id)}
                  className="sr-only"
                />
                <span
                  aria-hidden
                  className={cn(
                    'inline-flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px]',
                    checked ? 'border-accent' : 'border-line-strong',
                  )}
                >
                  {checked && <span className="size-2 rounded-full bg-accent" />}
                </span>
                <span className="min-w-0 flex-1 truncate text-fg">{c.name}</span>
              </label>
            );
          })}
        </fieldset>
      )}

      {issues.length > 0 && !saved && (
        <ul className="flex flex-col gap-1">
          {issues.map((a) => (
            <li key={a.code} className="flex items-start gap-1.5 text-caption text-warning">
              <TriangleAlert size={14} strokeWidth={1.75} aria-hidden className="mt-px shrink-0" />
              <span>{a.message}</span>
            </li>
          ))}
        </ul>
      )}

      {error && <FormMessage>{error}</FormMessage>}

      {saved ? (
        <p role="status" className="flex items-center gap-1.5 text-caption text-fg-secondary">
          <Check size={14} strokeWidth={2} aria-hidden className="text-success" />
          Zapisano wpis {saved}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="s"
            variant="primary"
            icon={Check}
            onClick={() => void save()}
            disabled={!preview.draft || choosing}
            loading={create.isPending}
          >
            Zapisz wpis
          </Button>
          {!preview.draft && (
            <span className="text-caption text-fg-muted">Uzupełnij dane albo wpisz czas ręcznie w module Czas.</span>
          )}
        </div>
      )}
    </div>
  );
}
